/*
  Endpoints do back-office.

  Até aqui este arquivo apontava para `admin.relinq.com.br`, que era um
  Express separado (back-office/back-end) rodando contra o MESMO banco do app,
  com 36 models duplicados e já divergentes. Esse backend deixou de existir:
  agora tudo é /api/admin na API principal.

  Duas consequências práticas:

  1. **A origem do front e da API são diferentes em produção.** O painel é
     servido em admin.relinq.com.br e chama app.relinq.com.br — chamada
     cross-origin, liberada pela allowlist em app/config/cors.js. Mudar o
     domínio do painel exige acrescentar o novo lá.

  2. **O token é de staff, não de profissional.** Chave de assinatura própria
     (JWT_STAFF_SECRET), então o token daqui não vale no app e o do app não
     vale aqui. Era o contrário antes: o segredo era o mesmo nos dois.

  `REACT_APP_API_URL` sobrescreve tudo — serve para apontar o painel para uma
  API de UAT sem recompilar com NODE_ENV mentindo.
*/

const resolveApiRoot = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, "");
  }

  // A API do app roda na 3002 em desenvolvimento (ver app/src/app.js).
  if (process.env.NODE_ENV === "development") {
    return "http://localhost:3002";
  }

  return "https://app.relinq.com.br";
};

// Tudo no painel é /api/admin: o baseURL do axios já carrega o prefixo, então
// as constantes abaixo são relativas a ele.
const BASE_URL = `${resolveApiRoot()}/api/admin`;

/*
  Origem do APP DO SALÃO — para onde a personificação manda o navegador.

  Não dá para derivar da URL da API: em produção as duas moram em
  app.relinq.com.br, mas em desenvolvimento a API está na 3002 e o app é outro
  servidor. `REACT_APP_APP_URL` aponta o painel de UAT para o app de UAT.

  Atenção em desenvolvimento: o app do salão e este painel são ambos
  configurados para a porta 8000 (`npm start` dos dois), então personificar
  localmente exige subir um deles em outra porta e informar esta variável.
*/
const resolveAppRoot = () => {
  if (process.env.REACT_APP_APP_URL) {
    return process.env.REACT_APP_APP_URL.replace(/\/+$/, "");
  }

  if (process.env.NODE_ENV === "development") {
    return "http://localhost:8000";
  }

  return "https://app.relinq.com.br";
};

/*
  O token da sessão de suporte entra por aqui: o app lê `?token=` da URL e o
  guarda (ver front-end/src/utils/utils.js, checkIfAuthenticated). Este
  endereço estava escrito à mão dentro de dois componentes, então apontar o
  painel para outro ambiente exigia editar os dois.
*/
const APP_LOGIN_URL = `${resolveAppRoot()}/login`;

// Login é a única rota fora do interceptor autenticado, então vai completa.
const AUTH = {
  LOGIN: `${BASE_URL}/login`,
  ME: "/me",
  CHANGE_PASSWORD: "/me/password",
};

// "Cliente" aqui é o SALÃO (Company). A cliente final do salão é Customer e
// não aparece neste painel a não ser como contagem e export.
const CLIENTS = {
  LIST: "/clients",
  // Busca do ClientPicker. Aberta ao operador, ao contrário da listagem.
  PICKER: "/clients/picker",
  EXPORT: "/clients/export",
  BULK: "/clients/bulk",
  CREATE: "/clients",
  ONE: "/clients", // + /:id
  FULL: "/clients", // + /:id/full
  WORKING_HOURS: "/clients", // + /:id/working-hours
  CATALOG: "/clients", // + /:id/catalog
  EXPORT_CUSTOMERS: "/clients", // + /:id/customers/export
};

const CRM = {
  STAGES: "/crm/stages", // PUT /:stage {label}: renomear a etapa (só admin; label vazio restaura o padrão)
  PIPELINE: "/crm/pipeline",
  PIPELINE_SYNC: "/crm/pipeline/sync",
  QUEUES: "/crm/queues",
  QUEUE: "/crm/queues", // + /:id
  TASKS: "/crm/tasks",
  TASK: "/crm/tasks", // + /:taskId
  CLIENT: "/crm/clients", // + /:id  (ficha de relacionamento)
  CLIENT_STAGE: "/crm/clients", // + /:id/stage  (PUT {stage}: arrastar o cartão, só admin)
  INTERACTIONS: "/crm/clients", // + /:id/interactions
  CLIENT_TASKS: "/crm/clients", // + /:id/tasks
  INTERACTION: "/crm/interactions", // + /:id  (pin, delete)
  FUNNEL: "/crm/funnel",
  FUNNEL_VELOCITY: "/crm/funnel/velocity",
  FUNNEL_ONBOARDING: "/crm/funnel/onboarding",
  FUNNEL_COHORTS: "/crm/funnel/cohorts",
};

// Marketing e notificações por e-mail. "campaigns" é disparo manual para um
// segmento; "triggers" é automático quando a conta entra num estágio.
const EMAIL = {
  STATUS: "/email/status",
  SEGMENT_OPTIONS: "/email/segment/options",
  SEGMENT_COUNT: "/email/segment/count",
  PREVIEW: "/email/preview",
  CAMPAIGNS: "/email/campaigns",
  CAMPAIGN: "/email/campaigns", // + /:id (+ /report, /test, /start, /pause...)
  TEMPLATES: "/email/templates",
  TRIGGERS: "/email/triggers",
  TRIGGER_PREVIEW: "/email/triggers/preview",
  SUPPRESSIONS: "/email/suppressions",
};

const DASHBOARDS = {
  OVERVIEW: "/overview",
  PORTFOLIO: "/portfolio",
};

const PROFESSIONALS = {
  LIST: "/professionals",
  ONE: "/professionals", // + /:id
  BY_EMAIL: "/professionals/email", // + /:email
  /*
    POST /professionals/:id/impersonate — cria a sessão de suporte e devolve o
    token do salão. Só ADMIN, e grava em SupportAuditLog antes de responder.

    Era o GET /professionals/:id que devolvia o token junto do cadastro, então
    todo staff que abria o formulário de edição recebia uma credencial viva do
    salão sem ninguém ter pedido nem registrado nada.
  */
  IMPERSONATE: "/professionals", // + /:id/impersonate
};

const PAYMENTS = {
  LIST: "/payments",
  HISTORY: "/payments/history",
  TAXES: "/payments/taxes",
};

const COUPONS = {
  LIST: "/coupons",
  ONE: "/coupons", // + /:id
};

const ANNOUNCEMENTS = {
  LIST: "/announcements",
  BROADCAST: "/announcements", // geral: o corpo precisa levar audience: "ALL"
  // Para UMA cliente (salão): a cliente vai no caminho, não no corpo.
  BROADCAST_COMPANY: "/announcements/company", // + /:companyId (+ /users: só alguns, corpo professionalIds)
  RECIPIENTS: "/announcements/recipients", // + ?companyId=<id>[&professionalIds=1,2]
  ONE: "/announcements", // + /:id
};

// Sugestões que as profissionais mandam pelo menu do app. Todo staff lê (o
// gerente de contas só as da carteira dele); decidir e juntar é de ADMIN/OPERATOR.
const SUGGESTIONS = {
  LIST: "/suggestions",
  ONE: "/suggestions", // + /:id (+ /merge)
};

// Aba "Eventos": os cadastros do onboarding do evento, com as respostas e o
// estado da cobrança. Tudo de ADMIN/OPERATOR no servidor.
// Eventos cadastrados (feira, workshop...): criar, vincular clientes e medir
// quantas vieram. Tudo de ADMIN/OPERATOR. `ONE` + /:id/clients é o vínculo.
const EVENTS = {
  LIST: "/events",
  OPTIONS: "/events/options",
  ONE: "/events", // + /:id  (GET, PUT, DELETE); + /:id/clients (POST {companyId, move}); + /:id/clients/:companyId (DELETE)
};

const EVENT_SIGNUPS = {
  LIST: "/event-signups", // ?eventId=<id>|none (um evento, ou os sem evento)
  PLAN_OPTIONS: "/event-signups", // + /:companyId/plan-options?users=<profissionais>
  PLAN: "/event-signups", // + /:companyId/plan  (PUT {planId, billingCycle, numberOfUsers})
};

// Pedidos de plano. A cobrança do evento é a aprovação do pedido: confirma o
// recebimento, lança o pagamento e libera o plano (ADMIN/OPERATOR).
const PLAN_REQUESTS = {
  APPROVE: "/plan-requests", // + /:id/approve  (POST {method, installments, value, note})
  POINT_CHARGE: "/plan-requests", // + /:id/point-charge  (POST {terminalId, type, amount}: envia à maquininha)
};

// Assinatura do salão (recorrência no Asaas). Criar, editar e cancelar é de
// ADMIN/OPERATOR no servidor; o gerente de contas só lê, e só a carteira dele.
const SUBSCRIPTIONS = {
  LIST: "/subscriptions", // ?status=&search=&page=&itemsPerPage=
  OPTIONS: "/subscriptions/options", // ?companyId=&users= (produtos e valores)
  FOR_COMPANY: "/subscriptions/company", // + /:companyId
  CREATE: "/subscriptions",
  ONE: "/subscriptions", // + /:id (PUT); + /:id/cancel, /:id/sync, /:id/send-link (POST)
};

// Maquininha Point da conta Mercado Pago da plataforma (a empresa em
// PLATFORM_POINT_COMPANY_ID no servidor). Cobrar plano com cartão passa por aqui;
// ADMIN/OPERATOR. Quem recebe nunca vem daqui: o servidor decide.
const POINT = {
  TERMINALS: "/point/terminals", // GET {configured, reason, terminals}; POST cadastra {mpTerminalId, name, switchToPdv}
  AVAILABLE_TERMINALS: "/point/terminals/available", // as da conta Mercado Pago, com registered/unavailable
  TERMINAL: "/point/terminals", // + /:id  (DELETE: desativa)
  CHARGE: "/point/charges", // + /:id (consulta) e /:id/cancel (POST)
};

const STAFF = {
  LIST: "/staff",
  OPTIONS: "/staff/options",
  ONE: "/staff", // + /:id
};

const TRAININGS = {
  LIST: "/trainings",
};

const AVATAR = {
  UPLOAD: "/avatar",
};

export {
  BASE_URL,
  APP_LOGIN_URL,
  AUTH,
  CLIENTS,
  CRM,
  EMAIL,
  DASHBOARDS,
  PROFESSIONALS,
  PAYMENTS,
  COUPONS,
  ANNOUNCEMENTS,
  SUGGESTIONS,
  EVENTS,
  EVENT_SIGNUPS,
  PLAN_REQUESTS,
  SUBSCRIPTIONS,
  POINT,
  STAFF,
  TRAININGS,
  AVATAR,
};
